import { constant, constVoid, date, flow, iots, option, pipe, tagged } from '@code-expert/prelude';
import type { OnlineStatus } from '/imports/domain';
import { Location } from '/imports/domain';
import { socket } from '/imports/startup/client/socket';

/**
 * Publish at least this often to keep the connection status alive.
 *
 * This needs to be slightly shorter than the TTL index we set on the Connections collection,
 * otherwise connections might get removed too early.
 */
const PUBLICATION_INTERVAL = { seconds: 50 };

/**
 *  User is marked as "away" after this timeout.
 */
const INACTIVITY_TIMEOUT = { minutes: 1 };

// -------------------------------------------------------------------------------------------------
// Init activity signals

socket.once('initSocket', () => {
  const dispatch = mkDispatch({ location: readLocation(), status: 'active' });

  trackLocation((location) => dispatch(adt.location(location)));

  trackActivity(date.milliseconds(INACTIVITY_TIMEOUT), 'active', (status) =>
    dispatch(adt[status]()),
  );
});

// -------------------------------------------------------------------------------------------------
// State updates

interface State {
  location: Location;
  status: OnlineStatus;
}

type Action =
  | tagged.Tagged<'active'>
  | tagged.Tagged<'away'>
  | tagged.Tagged<'idle'>
  | tagged.Tagged<'location', Location>;

const adt = tagged.build<Action>();

/**
 * Decide (via Option) whether an action needs to be published or not.
 */
const reducer = (state: State, action: Action): option.Option<State> =>
  adt.fold(action, {
    active: () =>
      state.status !== 'active' ? option.some<State>({ ...state, status: 'active' }) : option.none,
    away: () => (state.status !== 'away' ? option.some({ ...state, status: 'away' }) : option.none),
    idle: () => (state.status !== 'idle' ? option.some({ ...state, status: 'idle' }) : option.none),
    location: (location) =>
      state.location !== location ? option.some({ ...state, location }) : option.none,
  });

const mkDispatch = (state: State) => {
  const publicationinterval = date.milliseconds(PUBLICATION_INTERVAL);
  let lastPublished = 0;

  const publish = (nextState: State) => {
    lastPublished = date.now();
    // This procedure invocation is special:
    //   a) the invocation is not triggered by a user action, and
    //   b) it's ok to lose some events through the "volatile" option.
    // By making this a volatile invocation, we avoid crashes in case of a closed connection,
    // which does not only happen on network interruptions, but also when browsers close the
    // websocket after a period of inactivity to conserve energy.
    // See https://socket.io/docs/v4/emitting-events/#volatile-events
    socket.volatile.emit('invoke', 'users.connections.ping', nextState, constVoid);
    return nextState;
  };

  const dispatch = (a: Action) => {
    state = pipe(reducer(state, a), option.fold(constant(state), publish));
  };

  const heartbeatMonitor = () => {
    const nextScheduledHeartbeat = lastPublished + publicationinterval;
    if (pipe(date.now(), date.isAfter(nextScheduledHeartbeat))) {
      publish(state);
    }
  };

  setInterval(heartbeatMonitor, date.milliseconds({ seconds: 1 }));

  return dispatch;
};

// -------------------------------------------------------------------------------------------------
// Utils

const readLocation = () => pipe(location.pathname + location.search, iots.parseSync(Location));

/**
 * We observe the location through polling because there are no reliable ways of subscribing
 * to location change events at the time of writing.
 *
 * One alternative we tried was to proxy history push/replace, but it didn't prove reliable.
 */
const trackLocation = (onChange: (newLocation: Location) => void): void => {
  let location = readLocation();
  const checkLocationChange = () => {
    const current = readLocation();
    if (current !== location) {
      location = current;
      onChange(current);
    }
  };
  setInterval(checkLocationChange, date.milliseconds({ seconds: 1 }));
};

const trackActivity = (
  timeoutMs: number,
  initialStatus: OnlineStatus,
  onStatusChange: (status: OnlineStatus) => void,
): void => {
  let lastActive = initialStatus === 'active' ? date.now() : 0;
  let status: OnlineStatus = initialStatus;

  const setStatus = (s: OnlineStatus) => () => {
    if (status !== s) {
      status = s;
      onStatusChange(s);
    }
  };

  const updateLastActive = () => void (lastActive = date.now());

  // tab in or out
  window.addEventListener('focus', flow(updateLastActive, setStatus('active')));
  window.addEventListener('blur', setStatus('away'));

  // screen activity
  document.addEventListener('mousemove', flow(updateLastActive, setStatus('active')));
  document.addEventListener('mousedown', flow(updateLastActive, setStatus('active')));
  document.addEventListener('touchend', flow(updateLastActive, setStatus('active')));
  document.addEventListener('keydown', flow(updateLastActive, setStatus('active')));

  // activity timeout
  setInterval(
    () => {
      const timeout = lastActive + timeoutMs;
      if (pipe(date.now(), date.isAfter(timeout))) {
        setStatus('idle')();
      }
    },
    date.milliseconds({ seconds: 1 }),
  );
};
